import { Drawer } from '@mui/material'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useTolgee } from '@tolgee/react'
import {
  JobStepper,
  JobStepperHeader,
  useOperationsStore,
  YardSelectorProps,
} from '@tom-ui/operations'
import { orderService, RailcarAutocomplete } from '@tom-ui/planning'
import { ContainerShiftings, useStorageStore, YardOperationControl } from '@tom-ui/storage'
import { useYardStores, YardPositionSelector } from '@tom-ui/yard'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'

export const EquipmentOperatorStepper = observer(() => {
  const { equipmentOperatorStepperUIStore, tenantConfigStore, appStore, jobStore } =
    useOperationsStore()
  const { yardOperationControlStore } = useStorageStore()
  const { yardPositionSelectorStore } = useYardStores()
  const { containerShiftingsStore, containerShiftingsFormStore, snackbarStore } = useStorageStore()
  const { t } = useTolgee()

  const isNewYardEnabled = useBooleanFlagValue('new-yard', false)

  const renderYardPositionSelector = useCallback(
    ({
      hasError,
      containerNumber,
      onPositionChange,
      onBlockingIssueChange,
      journeyId,
      orderId,
    }: YardSelectorProps) => {
      if (isNewYardEnabled)
        return (
          <YardPositionSelector
            journeyId={journeyId!}
            hasError={hasError}
            prefillPosition='Reserved'
            onChange={(value: any) => onPositionChange?.(!!value)}
            onBlockingIssueChange={onBlockingIssueChange}
          />
        )

      return (
        <YardOperationControl
          containerNumber={containerNumber!}
          hasError={hasError}
          handleChange={(value: any) => onPositionChange?.(!!value)}
          orderId={orderId}
        />
      )
    },
    [isNewYardEnabled],
  )

  const handleConfirm = async (currentJob: any) => {
    let jobFinished = false
    const jobOriginType = equipmentOperatorStepperUIStore.getOriginType(
      currentJob.workInstructionId,
    )
    const linkedWorkInstructionOrCurrentId =
      currentJob.linkedWorkInstructionId ?? currentJob.workInstructionId
    const jobDestinationType =
      currentJob.destinationLocationType ??
      equipmentOperatorStepperUIStore.getDestinationType(linkedWorkInstructionOrCurrentId)
    const containerNumber = equipmentOperatorStepperUIStore.getContainerNumber(
      linkedWorkInstructionOrCurrentId,
    )

    try {
      if (currentJob.orderId && currentJob.grossWeight) {
        await orderService.updateWeight({
          id: currentJob.orderId,
          grossWeight: currentJob.grossWeight,
        })
      }

      if (
        currentJob.orderId &&
        jobDestinationType === 'Train' &&
        (tenantConfigStore.skipRailTally || tenantConfigStore.allowOperatorToSelectRailcar) &&
        currentJob.railcarId
      ) {
        const visitId = equipmentOperatorStepperUIStore.getVisitId(linkedWorkInstructionOrCurrentId)

        const railcarTrackPositionId = await orderService.createOrAssignOrderToRailcarTrackPosition(
          {
            orderId: currentJob.orderId,
            railVisitId: visitId,
            railcarId: currentJob.railcarId,
          },
        )

        currentJob.railcarTrackPositionId = railcarTrackPositionId
      }

      await equipmentOperatorStepperUIStore.finishJob(currentJob)
      jobFinished = true

      if (jobDestinationType === 'Yard') {
        if (isNewYardEnabled)
          await yardPositionSelectorStore.stackInJourney(currentJob.journeyId, containerNumber)
        else await yardOperationControlStore.onConfirm(containerNumber ?? '', currentJob.orderId)
      }

      if (jobOriginType === 'Yard') {
        containerShiftingsFormStore.resetFormItems()
        containerShiftingsStore.resetNumberOfShiftings()
      }
    } catch (ex: any) {
      equipmentOperatorStepperUIStore.equipmentOperatorUIStore.showJob(currentJob.workInstructionId)
      if (jobFinished) {
        await jobStore.cancelLastJob(currentJob.workInstructionId)
      } else {
        await equipmentOperatorStepperUIStore.cancelStartJob(currentJob.workInstructionId)
      }

      if (ex.response.data.status === 400 && ex.response.data.detail) {
        appStore.setShowAlert('error', ex.response.data.detail)
      }
    }
  }

  const handleOnClose = () => {
    containerShiftingsFormStore.resetFormItems()
  }

  const handlePicks = async (turnoverId: string) => {
    try {
      await containerShiftingsStore.updateContainerPosition(
        turnoverId,
        containerShiftingsFormStore.containerCurrentPositions.get(turnoverId)!,
      )
      equipmentOperatorStepperUIStore.setPicks(containerShiftingsStore.numberOfShiftingsDone)
      snackbarStore.showMessage(
        t('updateContainerPositionSuccess', 'The container position was successfully updated'),
        'success',
      )
      containerShiftingsFormStore.disableConfirmButton(turnoverId, true)
    } catch (ex: any) {
      snackbarStore.showMessage(
        t('updateContainerPositionWarning', 'The container position was not successfully updated'),
        'warning',
      )
    }
  }

  return (
    <Drawer
      open={equipmentOperatorStepperUIStore.isOpen}
      anchor='right'
      PaperProps={{
        sx: {
          width: '100vw',
        },
      }}
      data-cy='job-container-stepper'
    >
      <JobStepperHeader onConfirm={handleConfirm} onClose={handleOnClose} />

      <JobStepper
        yardSelector={renderYardPositionSelector}
        ConfirmPickupComponent={ContainerShiftings}
        RailcarAutocompleteComponent={RailcarAutocomplete}
        handlePicks={handlePicks}
      />
    </Drawer>
  )
})
